import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';

import { Locale, TokenType } from '@mfe/shared/schema-types';

import { getCurrentAddOns } from '../addOns/slice';
import { getHasPendingTransition } from '../changePlan';
import { FetchWithErrorsQuery, graphqlQueryWithErrors } from '../graphql';
import { selectLocale } from '../locale';
import { getCharacteristics } from '../plan';
import { getStreamEligibility } from '../streamOnPromo';
import { setAddress } from '../userInfo';
import { getWorkOrders } from '../workOrders';
import { fetchUserAction, selectUser, setAuth, setAuthUserError, setLocation, setUser } from './authUserSlice';
import { GET_BEP_INPUTS } from './query';
import { parseUser } from './utils';

type FetchUserInputType = {
  username?: string;
  partyId?: string;
  locale?: Locale;
};

export function* fetchUser() {
  const {
    user: {
      auth: {
        username,
        tokenInfo: { type },
      },
    },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  const {
    locale: { userLocale },
  }: ReturnType<typeof selectLocale> = yield select(selectLocale);

  const input: FetchUserInputType = type === TokenType.Salesforce ? { partyId: username } : { username };
  if (userLocale) input.locale = userLocale;

  const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_BEP_INPUTS,
    variables: { refetchData: true },
    fetchPolicy: 'network-only',
  });
  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors) {
    yield put(setAuthUserError(runtimeError ?? errors));
    return;
  }

  if (data?.getBEPInputs) {
    const {
      accountNumber,
      productInstanceId,
      productTypeId,
      productInstanceStatus,
      productKind,
      partyId,
      relnId,
      serviceAddress,
      coordinates,
      isSoftSuspended,
      hasCaf,
      hasErrorProductInstance,
      indoorUnit,
      isFiberPreconnected,
      fiberAddressId,
      fiberCityId,
    } = parseUser(data);

    yield put(
      setUser({
        accountNumber,
        productInstanceId,
        productTypeId,
        productInstanceStatus,
        productKind,
        partyId,
        relnId,
        isSoftSuspended,
        hasCaf,
        hasErrorProductInstance,
        indoorUnit,
        isFiberPreconnected: isFiberPreconnected ?? false,
        fiberAddressId,
        fiberCityId,
      })
    );

    yield put(setLocation({ address: serviceAddress, coordinates }));

    yield put(getCurrentAddOns());
    yield put(getStreamEligibility());
    yield put(getHasPendingTransition());
    yield put(setAddress({ service: serviceAddress }));
    yield put(getWorkOrders());
    yield put(getCharacteristics());
  }
}

export function* waitForUserData() {
  const { loading } = yield select(selectUser);

  if (!loading) {
    yield take(setUser.type);
  }
}

export function* watchUser() {
  yield all([takeLatest(setAuth.type, fetchUser), takeLatest(fetchUserAction.type, fetchUser)]);
}
